//@flow

import {TRIGGER_FIELDS} from '../components/TriggerForm/const';
import {FILTER_TYPES} from './triggerConfig';
import type {TriggerFilterType} from '../../../../entities';

const FILTER_NESTED_FIELDS: {[TriggerFilterType]: string[]} = {
  [FILTER_TYPES.TRIGGER_TEXT_SEARCH]: [
    'searchText',
    'searchTextPattern',
    'enabledRelatedTexts',
    'relatedSearchTexts',
  ],
  [FILTER_TYPES.CUSTOMER_INACTIVITY]: ['inactiveDays'],
  [FILTER_TYPES.CRM_SEGMENT]: ['segmentId'],
  [FILTER_TYPES.CRM_TAGS]: ['tagIds'],
  [FILTER_TYPES.CRM_FUNNEL]: ['funnelId', 'funnelLaneId'],
};

export const FILTER_SET_VALUE_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
};

export function getFilterFieldPaths(filterType: TriggerFilterType): string[] {
  const prefix = `${TRIGGER_FIELDS.filters}.${filterType}`;
  const nestedFields = FILTER_NESTED_FIELDS[filterType] || [];

  return [prefix, ...nestedFields.map(field => `${prefix}.${field}`)];
}

export function unregisterFilterFields(
  filterType: TriggerFilterType,
  unregister: (name: string) => void
) {
  getFilterFieldPaths(filterType).forEach(path => {
    unregister(path);
  });
}

/**
 * RHF v6.3.3: child filter components register nested `filters.*` paths, while
 * FiltersSection also registers the parent `filters` object. Children mount
 * (and register) before the parent, so getValues() runs transformToNestObject
 * with `filters` last and overwrites nested edits with the stale parent object.
 *
 * Always read-modify-write the whole `filters` object (parent setValue last).
 */
export function setFilterFieldUpdates(
  getValues: (name?: string) => any,
  setValue: (name: string, value: any, options?: any) => void,
  filterType: TriggerFilterType,
  fieldUpdates: {[key: string]: any},
  options: any = FILTER_SET_VALUE_OPTIONS
) {
  // Use full getValues() — getValues('filters') returns only the parent field
  // ref and can miss nested edits when both parent + nested paths are registered.
  const allValues = getValues() || {};
  const filters = {...(allValues[TRIGGER_FIELDS.filters] || {})};
  const currentFilter = {...(filters[filterType] || {})};
  const nextFilter = {
    ...currentFilter,
    ...fieldUpdates,
  };
  const nextFilters = {
    ...filters,
    [filterType]: nextFilter,
  };

  const prefix = `${TRIGGER_FIELDS.filters}.${filterType}`;
  Object.keys(fieldUpdates).forEach(fieldName => {
    setValue(`${prefix}.${fieldName}`, fieldUpdates[fieldName], options);
  });
  // Parent last — wins transformToNestObject overwrite in getValues().
  setValue(TRIGGER_FIELDS.filters, nextFilters, options);
}
